import { useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "@/components/ui/icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { fmtMoney } from "@/lib/format";
import { extractApiErrorMessage } from "@/lib/api";
import { closePairPositions, closePosition, modifyPosition } from "@/lib/api/manual-trade";
import type { PositionLive } from "@/store/dashboard.store";

/**
 * Compact positions table for the Terminal. Mirrors the pair-detail
 * PositionsSection UX — live rows from the Zustand store — but uses
 * the REST `manual-trade/close` + `manual-trade/modify` endpoints
 * instead of the WS DashboardCommand path. This keeps the Terminal
 * self-contained and auditable: every action hits a Hono route that
 * writes an `audit_log` row before the frame leaves the worker.
 */
export function PositionsMiniTable({
  pairId,
  livePositions,
  allowedAccountIds,
  firmNameFor,
}: {
  pairId: string;
  livePositions: Record<string, PositionLive> | null;
  allowedAccountIds: Set<string>;
  firmNameFor(accountId: string): string;
}): ReactNode {
  const rows = Object.values(livePositions ?? {})
    .filter((p) => allowedAccountIds.has(p.accountId))
    .sort(
      (a, b) =>
        a.accountId.localeCompare(b.accountId) || a.ticket - b.ticket,
    );

  const [editing, setEditing] = useState<PositionLive | null>(null);
  const [slInput, setSlInput] = useState("");
  const [tpInput, setTpInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [closingTicket, setClosingTicket] = useState<number | null>(null);
  const [closeAllOpen, setCloseAllOpen] = useState(false);

  const openModify = (p: PositionLive) => {
    setEditing(p);
    setSlInput(p.stopLoss == null ? "" : String(p.stopLoss));
    setTpInput(p.takeProfit == null ? "" : String(p.takeProfit));
  };

  const submitModify = async () => {
    if (!editing) return;
    const slParsed = Number(slInput);
    const tpParsed = tpInput.trim() === "" ? null : Number(tpInput);
    if (!(Number.isFinite(slParsed) && slParsed > 0)) {
      toast.error("SL is required and must be a positive number.");
      return;
    }
    if (tpParsed !== null && !(Number.isFinite(tpParsed) && tpParsed > 0)) {
      toast.error("TP must be a positive number (or empty to remove).");
      return;
    }
    setBusy(true);
    try {
      await modifyPosition({
        accountId: editing.accountId,
        ticket: editing.ticket,
        sl: slParsed,
        tp: tpParsed,
      });
      toast.success(`Modify sent · ticket #${editing.ticket}`);
      setEditing(null);
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const submitClose = async (p: PositionLive) => {
    setClosingTicket(p.ticket);
    try {
      await closePosition({ accountId: p.accountId, ticket: p.ticket });
      toast.success(`Close sent · ticket #${p.ticket}`);
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setClosingTicket(null);
    }
  };

  const submitCloseAll = async () => {
    setBusy(true);
    try {
      const result = await closePairPositions(pairId);
      if (result.ok) {
        toast.success(`Close all sent · ${rows.length} open position${rows.length === 1 ? "" : "s"}`);
      } else {
        toast.error(result.error ?? "Close all did not dispatch.");
      }
      setCloseAllOpen(false);
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-card/60">
      <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
            Open Positions
          </span>
          <span className="font-mono text-[10px] text-muted-foreground">
            {rows.length} live
          </span>
        </div>
        {rows.length > 0 && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setCloseAllOpen(true)}
            disabled={busy}
            className="h-7 border-destructive/40 px-2 text-[10px] text-destructive hover:bg-destructive/10"
          >
            Close All
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="py-6 text-center text-xs text-muted-foreground">
          No open positions on this pair’s legs.
        </div>
      ) : (
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-xs">
            <thead className="bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Firm</th>
                <th className="px-3 py-2 text-left">Ticket</th>
                <th className="px-3 py-2 text-left">Symbol</th>
                <th className="px-3 py-2 text-left">Side</th>
                <th className="px-3 py-2 text-right">Lots</th>
                <th className="px-3 py-2 text-right">Entry</th>
                <th className="px-3 py-2 text-right">SL</th>
                <th className="px-3 py-2 text-right">TP</th>
                <th className="px-3 py-2 text-right">P&amp;L</th>
                <th className="px-3 py-2 text-right" />
              </tr>
            </thead>
            <tbody>
              <AnimatePresence initial={false}>
                {rows.map((p) => (
                  <motion.tr
                    key={`${p.accountId}:${p.ticket}`}
                    layout
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    className="border-t border-border/40"
                  >
                    <td className="px-3 py-2 text-foreground">{firmNameFor(p.accountId)}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                      #{p.ticket}
                    </td>
                    <td className="px-3 py-2 font-mono">{p.symbol}</td>
                    <td className="px-3 py-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[10px] uppercase",
                          p.side === "buy"
                            ? "border-emerald-500/30 text-emerald-500"
                            : "border-rose-500/30 text-rose-500",
                        )}
                      >
                        {p.side}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{p.lots}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{p.entry}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-500">
                      {p.stopLoss ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-500/80">
                      {p.takeProfit ?? "—"}
                    </td>
                    <td
                      className={cn(
                        "px-3 py-2 text-right font-mono tabular-nums font-semibold",
                        p.openPnl >= 0 ? "text-emerald-500" : "text-rose-500",
                      )}
                    >
                      {p.openPnl >= 0 ? "+" : ""}
                      {fmtMoney(p.openPnl)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => openModify(p)}
                          disabled={busy || closingTicket === p.ticket}
                          className="h-6 px-2 text-[10px]"
                        >
                          Modify
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => submitClose(p)}
                          disabled={busy || closingTicket === p.ticket}
                          aria-busy={closingTicket === p.ticket}
                          className="h-6 border-destructive/40 px-2 text-[10px] text-destructive hover:bg-destructive/10"
                        >
                          {closingTicket === p.ticket && (
                            <Loader2 aria-hidden="true" className="mr-0.5 h-3 w-3 animate-spin" />
                          )}
                          {closingTicket === p.ticket ? "Closing…" : "Close"}
                        </Button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && (
        <ul className="flex flex-col gap-2 p-3 md:hidden">
          <AnimatePresence initial={false}>
            {rows.map((p) => (
              <motion.li
                key={`mobile:${p.accountId}:${p.ticket}`}
                layout
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="flex flex-col gap-2 rounded-lg border border-border/40 bg-muted/20 p-3 text-xs"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-[10px] uppercase",
                        p.side === "buy"
                          ? "border-emerald-500/30 text-emerald-500"
                          : "border-rose-500/30 text-rose-500",
                      )}
                    >
                      {p.side}
                    </Badge>
                    <span className="truncate text-sm font-medium text-foreground">
                      {p.symbol}
                    </span>
                    <span className="font-mono text-[10px] text-muted-foreground">
                      #{p.ticket}
                    </span>
                  </div>
                  <span
                    className={cn(
                      "font-mono text-sm font-semibold tabular-nums",
                      p.openPnl >= 0 ? "text-emerald-500" : "text-rose-500",
                    )}
                  >
                    {p.openPnl >= 0 ? "+" : ""}
                    {fmtMoney(p.openPnl)}
                  </span>
                </div>
                <div className="text-[11px] text-muted-foreground">{firmNameFor(p.accountId)}</div>
                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <div>
                    <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">Lots</span>
                    <span className="font-mono tabular-nums">{p.lots}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">Entry</span>
                    <span className="font-mono tabular-nums">{p.entry}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">SL / TP</span>
                    <span className="font-mono tabular-nums text-amber-500">
                      {p.stopLoss ?? "—"}
                    </span>
                    <span className="text-muted-foreground"> / </span>
                    <span className="font-mono tabular-nums text-emerald-500/80">
                      {p.takeProfit ?? "—"}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 flex-1 text-[11px]"
                    onClick={() => openModify(p)}
                    disabled={busy || closingTicket === p.ticket}
                  >
                    Modify
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 flex-1 border-destructive/40 text-[11px] text-destructive hover:bg-destructive/10"
                    onClick={() => submitClose(p)}
                    disabled={busy || closingTicket === p.ticket}
                    aria-busy={closingTicket === p.ticket}
                  >
                    {closingTicket === p.ticket && (
                      <Loader2 aria-hidden="true" className="mr-1 h-3 w-3 animate-spin" />
                    )}
                    {closingTicket === p.ticket ? "Closing…" : "Close"}
                  </Button>
                </div>
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>
      )}

      <Dialog
        open={editing != null}
        onOpenChange={(o) => {
          if (!o) setEditing(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modify SL / TP</DialogTitle>
            <DialogDescription>
              {editing
                ? `Update the bracket on ticket #${editing.ticket} (${firmNameFor(editing.accountId)}). Empty = remove.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <label className="flex flex-col gap-2">
              <span className="text-xs font-medium text-muted-foreground">
                Stop Loss <span className="text-destructive">*</span>
              </span>
              <Input
                value={slInput}
                onChange={(e) => setSlInput(e.target.value)}
                placeholder="required"
                inputMode="decimal"
              />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-xs font-medium text-muted-foreground">
                Take Profit
              </span>
              <Input
                value={tpInput}
                onChange={(e) => setTpInput(e.target.value)}
                placeholder="empty = remove"
                inputMode="decimal"
              />
            </label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditing(null)} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" onClick={submitModify} disabled={busy} aria-busy={busy}>
              {busy && (
                <Loader2 aria-hidden="true" className="mr-1 h-4 w-4 animate-spin" />
              )}
              {busy ? "Sending…" : "Send Modify"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={closeAllOpen} onOpenChange={setCloseAllOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Close All Hedge Legs</DialogTitle>
            <DialogDescription>
              This sends a force-close command to both accounts in the pair.
              Individual close stays available for rescue cases.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-destructive/25 bg-destructive/5 p-3 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">{rows.length}</span>{" "}
            open position{rows.length === 1 ? "" : "s"} will be closed across
            this hedge pair.
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCloseAllOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={submitCloseAll}
              disabled={busy}
              aria-busy={busy}
            >
              {busy && (
                <Loader2 aria-hidden="true" className="mr-1 h-4 w-4 animate-spin" />
              )}
              {busy ? "Sending…" : "Close All"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
